// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React from 'react';
import styled from '@emotion/styled';
import { UilFacebookF as FacebookIcon, UilLinkedin } from '@iconscout/react-unicons';
import { Link } from 'react-navi';

import { useTheme } from '@emotion/react';
import { Typography } from '@kintent/glide';
import { Flex } from './Flex';
import { useAuthService } from '../lib/state';
import { BREAKPOINTS, FACEBOOK_APP_ID } from '../lib/constants';

// Assets
import { ReactComponent as TwitterLogo } from '../assets/home/twitter-logo.svg';

const SocialMediaBlurb = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 12px;

  & > h6 {
    width: 260px;
    text-align: center;
  }

  @media (min-width: ${BREAKPOINTS.TABLET}px) {
    & {
      align-items: flex-end;

      & > h6 {
        width: fit-content;
        text-align: initial;
      }
    }
  }
`;

const SocialMediaIconLink = styled(Link)`
  &:hover {
    transform: translateY(-2px);
  }

  transition: transform 0.15s ease-in;
`;

const TwitterIcon = styled(TwitterLogo)`
  fill: ${({ theme }) => theme.components.header.primaryCTAButton.background};
`;

function SocialMediaFooterLinks() {
  const { currentTeam } = useAuthService();
  const theme = useTheme();

  let url;
  let uriEncodedUrl;
  let uriEncodedShareText;

  if (currentTeam) {
    url = currentTeam?.trustShareUrl;
    uriEncodedUrl = encodeURIComponent(url);
    uriEncodedShareText = encodeURIComponent(
      `Kudos to ${currentTeam?.name} for being transparent about their security and privacy program. Goes a long way in earning customer #Trust. Check out their #TrustShare here -`
    );
  }

  return (
    <SocialMediaBlurb>
      <Flex
        gap="20px"
        justifyContent="flex-end"
      >
        <Flex
          href={`https://www.facebook.com/dialog/share?app_id=${FACEBOOK_APP_ID}&title=${
            currentTeam?.name
          }&display=page&hashtag=${encodeURIComponent(`#${currentTeam?.name}`)}&href=${uriEncodedUrl}`}
          as={SocialMediaIconLink}
          target="_blank"
        >
          <FacebookIcon
            size={24}
            fill={theme.components.header.primaryCTAButton.background}
          />
        </Flex>
        <Flex
          href={`https://twitter.com/intent/tweet?text=${uriEncodedShareText}&url=${uriEncodedUrl}`}
          as={SocialMediaIconLink}
          target="_blank"
        >
          <TwitterIcon
            width={24}
            height={24}
          />
        </Flex>
        <Flex
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${uriEncodedUrl}`}
          as={SocialMediaIconLink}
          target="_blank"
        >
          <UilLinkedin
            size={24}
            fill={theme.components.header.primaryCTAButton.background}
          />
        </Flex>
      </Flex>
      {currentTeam && (
        <Typography
          level="8"
          strength="60%"
        >
          Give {currentTeam.name} a shout-out about their transparency
        </Typography>
      )}
    </SocialMediaBlurb>
  );
}

export default SocialMediaFooterLinks;
